import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, ExternalLink, CheckCircle2, Circle, Clock } from "lucide-react";
import { API, CITIES, CONTENT_PLAN, PRIORITY_COLORS } from "./constants";

export function ContentPlanTab({ password, onCreateArticle }) {
  const [existingArticles, setExistingArticles] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCity, setExpandedCity] = useState(null);

  useEffect(() => {
    const fetchExisting = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/articles`, { params: { password } });
        const map = {};
        for (const a of res.data) {
          map[a.slug] = { status: a.status, id: a.id };
        }
        setExistingArticles(map);
      } catch {
        toast.error("Ошибка загрузки статей");
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
  }, [password]);

  const totalPlanned = CONTENT_PLAN.reduce((sum, g) => sum + g.articles.length, 0);
  const totalPublished = CONTENT_PLAN.reduce(
    (sum, g) => sum + g.articles.filter(a => existingArticles[a.slug]?.status === "published").length,
    0
  );
  const totalDraft = CONTENT_PLAN.reduce(
    (sum, g) => sum + g.articles.filter(a => existingArticles[a.slug]?.status === "draft").length,
    0
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  return (
    <div data-testid="content-plan-tab">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Контент-план</h1>
        <p className="text-gray-500 text-sm mt-1">SEO-статьи для органического трафика из Google</p>
      </div>

      {/* Progress overview */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 text-center" data-testid="plan-total">
          <div className="text-3xl font-bold text-gray-900">{totalPublished}/{totalPlanned}</div>
          <div className="text-xs text-gray-500 mt-1 font-medium">Опубликовано</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 text-center" data-testid="plan-drafts">
          <div className="text-3xl font-bold text-amber-500">{totalDraft}</div>
          <div className="text-xs text-gray-500 mt-1 font-medium">Черновиков</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 text-center" data-testid="plan-remaining">
          <div className="text-3xl font-bold text-gray-400">{totalPlanned - totalPublished - totalDraft}</div>
          <div className="text-xs text-gray-500 mt-1 font-medium">Не начато</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-gray-100 rounded-full h-3 mb-8 overflow-hidden">
        <div
          className="bg-gradient-to-r from-pink-500 to-rose-500 h-full rounded-full transition-all duration-500"
          style={{ width: `${(totalPublished / totalPlanned) * 100}%` }}
        />
      </div>

      {/* City groups */}
      <div className="space-y-4">
        {CONTENT_PLAN.map((group) => {
          const published = group.articles.filter(a => existingArticles[a.slug]?.status === "published").length;
          const drafts = group.articles.filter(a => existingArticles[a.slug]?.status === "draft").length;
          const isExpanded = expandedCity === group.city || expandedCity === null;

          return (
            <div key={group.city || "general"} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`plan-group-${group.city || "general"}`}>
              {/* Group header */}
              <button
                onClick={() => setExpandedCity(expandedCity === group.city ? "__none__" : group.city)}
                className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${PRIORITY_COLORS[group.priority]}`}>
                    {group.priority}
                  </span>
                  <h2 className="font-bold text-gray-900">{group.cityName}</h2>
                  <span className="text-sm text-gray-400">
                    {published}/{group.articles.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Mini progress */}
                  <div className="w-24 bg-gray-100 rounded-full h-2 hidden sm:block">
                    <div
                      className="bg-green-500 h-full rounded-full transition-all"
                      style={{ width: `${(published / group.articles.length) * 100}%` }}
                    />
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Articles list */}
              {isExpanded && (
                <div className="border-t border-gray-50">
                  {group.articles.map((article, idx) => {
                    const existing = existingArticles[article.slug];
                    const isPublished = existing?.status === "published";
                    const isDraft = existing?.status === "draft";

                    return (
                      <div
                        key={article.slug}
                        className={`flex items-center gap-3 px-5 py-3.5 border-b border-gray-50 last:border-b-0 ${isPublished ? "bg-green-50/40" : ""}`}
                        data-testid={`plan-article-${article.slug}`}
                      >
                        {/* Status icon */}
                        <div className="flex-shrink-0">
                          {isPublished ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : isDraft ? (
                            <Clock className="w-5 h-5 text-amber-400" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-300" />
                          )}
                        </div>

                        {/* Article info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-mono w-5">{idx + 1}.</span>
                            <span className={`text-sm font-medium truncate ${isPublished ? "text-green-700" : isDraft ? "text-amber-700" : "text-gray-700"}`}>
                              {article.titleRu}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 ml-7">
                            <span className="text-xs text-gray-400 font-mono">/{article.slug}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex-shrink-0 flex items-center gap-2">
                          {isPublished ? (
                            <a
                              href={`/blog/${article.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-green-600 hover:text-green-700 flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Открыть
                            </a>
                          ) : isDraft ? (
                            <button
                              onClick={() => onCreateArticle(article, group.city)}
                              className="text-xs font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1"
                            >
                              <Pencil className="w-3 h-3" />
                              Редактировать
                            </button>
                          ) : (
                            <button
                              onClick={() => onCreateArticle(article, group.city)}
                              className="text-xs font-semibold text-pink-500 hover:text-pink-600 flex items-center gap-1"
                              data-testid={`plan-create-${article.slug}`}
                            >
                              <Plus className="w-3 h-3" />
                              Написать
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
